import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "./api/client";
import AccountSettingSelect from "./components/AccountSettingSelect";
import SettingsSavedNotice from "./components/SettingsSavedNotice";
import { useCurrentUser } from "./currentUserContext";
import {
  WANTS_CONDITION_OPTIONS,
  WANTS_LANGUAGE_OPTIONS,
  WANTS_BOOLEAN_OPTIONS,
  WANTS_ANY_VALUE,
  getConditionColor,
  getLanguageCountryCode,
  getWantsBooleanLabel,
  getWantsConditionLabel,
  getWantsLanguageLabel
} from "./wantsSettingsOptions";

function normalizeLanguage(value) {
  return String(value || "en").trim().toLowerCase() || "en";
}

function normalizeCondition(value) {
  return String(value || "NM").trim().toUpperCase() || "NM";
}

function normalizeBooleanSetting(value) {
  const normalizedValue = String(value || "__any__").trim().toLowerCase();

  if (["true", "false", "__any__"].includes(normalizedValue)) {
    return normalizedValue;
  }

  if (normalizedValue === "any") {
    return "__any__";
  }

  return "__any__";
}

function renderLanguageValue(value) {
  if (value === WANTS_ANY_VALUE) {
    return <span className="account-setting-select-preview-label">Any language</span>;
  }

  const countryCode = getLanguageCountryCode(value);

  return (
    <>
      <span className={`fi fi-${countryCode} account-setting-select-preview-flag`} aria-hidden="true" />
      <span className="account-setting-select-preview-label">{getWantsLanguageLabel(value)}</span>
    </>
  );
}

function renderConditionValue(value) {
  if (value === WANTS_ANY_VALUE) {
    return <span className="account-setting-select-preview-label">Any condition</span>;
  }

  const conditionCode = String(value || "NM").toUpperCase();
  const conditionColor = getConditionColor(conditionCode);

  return (
    <>
      <span
        className="account-setting-select-preview-badge"
        style={{
          background: conditionColor,
          color: conditionCode === "GD" ? "#111" : "#fff"
        }}
      >
        {conditionCode}
      </span>
      <span className="account-setting-select-preview-label">{getWantsConditionLabel(conditionCode)}</span>
    </>
  );
}

function renderBooleanValue(value) {
  return <span className="account-setting-select-preview-label">{getWantsBooleanLabel(value)}</span>;
}

function SelectSettingCard({ title, description, value, options, onChange, renderSelectedValue }) {
  return (
    <section className="account-setting-card">
      <div className="account-setting-card__header">
        <div>
          <h2 className="account-setting-card__title">{title}</h2>
          <p className="account-setting-card__copy">{description}</p>
        </div>
      </div>

      <div className="account-setting-card__body">
        <AccountSettingSelect
          title={title}
          value={value}
          options={options}
          onChange={onChange}
          renderSelectedValue={renderSelectedValue}
          renderOption={renderSelectedValue}
        />
      </div>
    </section>
  );
}

export default function MyWantsSettingsView() {
  const { selectedUserId, selectedUser } = useCurrentUser();
  const [savedSettings, setSavedSettings] = useState({ defaultLanguage: "en", defaultCondition: "NM", defaultFoil: "__any__", defaultAltered: "__any__", defaultSigned: "__any__" });
  const [draft, setDraft] = useState({ defaultLanguage: "en", defaultCondition: "NM", defaultFoil: "__any__", defaultAltered: "__any__", defaultSigned: "__any__" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    let isDisposed = false;

    async function loadSettings() {
      try {
        setLoading(true);
        const response = await apiFetch(`/api/account/wants-settings/${selectedUserId}`, { userId: selectedUserId });
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || "Could not load My Wants settings");
        }

        if (isDisposed) {
          return;
        }

        const nextSettings = {
          defaultLanguage: normalizeLanguage(data?.settings?.defaultLanguage),
          defaultCondition: normalizeCondition(data?.settings?.defaultCondition),
          defaultFoil: normalizeBooleanSetting(data?.settings?.defaultFoil),
          defaultAltered: normalizeBooleanSetting(data?.settings?.defaultAltered),
          defaultSigned: normalizeBooleanSetting(data?.settings?.defaultSigned)
        };

        setSavedSettings(nextSettings);
        setDraft(nextSettings);
        setError(null);
      } catch (err) {
        if (!isDisposed) {
          console.error("My Wants settings load error:", err);
          setError(err.message || "Could not load My Wants settings");
        }
      } finally {
        if (!isDisposed) {
          setLoading(false);
        }
      }
    }

    loadSettings();

    return () => {
      isDisposed = true;
    };
  }, [selectedUserId]);

  const submissionSettings = useMemo(() => ({
    defaultLanguage: normalizeLanguage(draft.defaultLanguage),
    defaultCondition: normalizeCondition(draft.defaultCondition),
    defaultFoil: normalizeBooleanSetting(draft.defaultFoil),
    defaultAltered: normalizeBooleanSetting(draft.defaultAltered),
    defaultSigned: normalizeBooleanSetting(draft.defaultSigned)
  }), [draft]);

  const isDirty = submissionSettings.defaultLanguage !== savedSettings.defaultLanguage
    || submissionSettings.defaultCondition !== savedSettings.defaultCondition
    || submissionSettings.defaultFoil !== savedSettings.defaultFoil
    || submissionSettings.defaultAltered !== savedSettings.defaultAltered
    || submissionSettings.defaultSigned !== savedSettings.defaultSigned;

  useEffect(() => {
    if (!notice) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => setNotice(null), 3600);
    return () => window.clearTimeout(timeoutId);
  }, [notice]);

  useEffect(() => {
    if (loading || saving || !isDirty) {
      return undefined;
    }

    const timeoutId = window.setTimeout(async () => {
      try {
        setSaving(true);
        setError(null);
        setNotice(null);
        const response = await apiFetch(`/api/account/wants-settings/${selectedUserId}`, {
          userId: selectedUserId,
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(submissionSettings)
        });
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || "Could not save My Wants settings");
        }

        const nextSettings = {
          defaultLanguage: normalizeLanguage(data?.settings?.defaultLanguage),
          defaultCondition: normalizeCondition(data?.settings?.defaultCondition),
          defaultFoil: normalizeBooleanSetting(data?.settings?.defaultFoil),
          defaultAltered: normalizeBooleanSetting(data?.settings?.defaultAltered),
          defaultSigned: normalizeBooleanSetting(data?.settings?.defaultSigned)
        };

        setSavedSettings(nextSettings);
        setNotice("Settings saved");
      } catch (err) {
        console.error("My Wants settings save error:", err);
        setError(err.message || "Could not save My Wants settings");
      } finally {
        setSaving(false);
      }
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [isDirty, loading, saving, selectedUserId, submissionSettings]);

  return (
    <div className="account-shell">
      <div className="account-settings-page">
        <div className="account-breadcrumb">
          <Link to="/account">My Account</Link>
          <span>/</span>
          <span>My Wants Settings</span>
        </div>

        <div className="account-settings-panel__header">
          <div>
            <h1 className="account-title">My Wants Settings</h1>
            <p className="account-copy">
              Choose the default language, condition and finish preferences used when {selectedUser?.label || "this user"} adds or imports wanted cards.
            </p>
          </div>
        </div>

        {error ? <div className="account-error">{error}</div> : null}

        {loading ? (
          <div className="account-loading">Loading settings...</div>
        ) : (
          <>
            <div className="account-setting-grid account-setting-grid--stacked-mobile">
              <SelectSettingCard
                title="Default language"
                description="Preselect the language used when adding cards to My Wants. You can still adjust it in the add modal."
                value={draft.defaultLanguage}
                options={WANTS_LANGUAGE_OPTIONS}
                renderSelectedValue={renderLanguageValue}
                onChange={(nextValue) => setDraft((currentState) => ({ ...currentState, defaultLanguage: normalizeLanguage(nextValue) }))}
              />

              <SelectSettingCard
                title="Default condition"
                description="Choose the default card condition that appears when adding new wanted cards from search."
                value={draft.defaultCondition}
                options={WANTS_CONDITION_OPTIONS}
                renderSelectedValue={renderConditionValue}
                onChange={(nextValue) => setDraft((currentState) => ({ ...currentState, defaultCondition: normalizeCondition(nextValue) }))}
              />

              <SelectSettingCard
                title="Default foil"
                description="Choose the default foil state for new wanted cards. Explicit foil values in CSV or text import override this default."
                value={draft.defaultFoil}
                options={WANTS_BOOLEAN_OPTIONS}
                renderSelectedValue={renderBooleanValue}
                onChange={(nextValue) => setDraft((currentState) => ({ ...currentState, defaultFoil: normalizeBooleanSetting(nextValue) }))}
              />

              <SelectSettingCard
                title="Default altered"
                description="Choose the default altered state for new wanted cards and imports."
                value={draft.defaultAltered}
                options={WANTS_BOOLEAN_OPTIONS}
                renderSelectedValue={renderBooleanValue}
                onChange={(nextValue) => setDraft((currentState) => ({ ...currentState, defaultAltered: normalizeBooleanSetting(nextValue) }))}
              />

              <SelectSettingCard
                title="Default signed"
                description="Choose the default signed state for new wanted cards and imports."
                value={draft.defaultSigned}
                options={WANTS_BOOLEAN_OPTIONS}
                renderSelectedValue={renderBooleanValue}
                onChange={(nextValue) => setDraft((currentState) => ({ ...currentState, defaultSigned: normalizeBooleanSetting(nextValue) }))}
              />
            </div>
          </>
        )}
      </div>

      {notice ? <SettingsSavedNotice message={notice} /> : null}
    </div>
  );
}